import { Stack, ToggleButton, ToggleButtonGroup } from "@mui/material";
import { useRef, type KeyboardEvent, type MouseEvent } from "react";

export interface SegmentedOption<V extends string> {
  value: V;
  label: string;
}

interface SegmentedRadioGroupProps<V extends string> {
  ariaLabel: string;
  options: ReadonlyArray<SegmentedOption<V>>;
  value: V | undefined;
  onChange: (value: V | undefined) => void;
  allowDeselect?: boolean;
}

export function SegmentedRadioGroup<V extends string>({
  ariaLabel,
  options,
  value,
  onChange,
  allowDeselect = false,
}: SegmentedRadioGroupProps<V>) {
  const groupRef = useRef<HTMLDivElement>(null);
  const checkedIndex = options.findIndex((option) => option.value === value);
  const tabbableIndex = checkedIndex >= 0 ? checkedIndex : 0;

  function focusRadioAt(index: number) {
    groupRef.current
      ?.querySelectorAll<HTMLElement>('[role="radio"]')
      [index]?.focus();
  }

  function selectAt(index: number) {
    onChange(options[index].value);
    focusRadioAt(index);
  }

  function onKeyDown(event: KeyboardEvent<HTMLDivElement>) {
    if (options.length === 0) return;
    const from = checkedIndex >= 0 ? checkedIndex : 0;
    let next: number;
    switch (event.key) {
      case "ArrowRight":
      case "ArrowDown":
        next = (from + 1) % options.length;
        break;
      case "ArrowLeft":
      case "ArrowUp":
        next = (from - 1 + options.length) % options.length;
        break;
      case "Home":
        next = 0;
        break;
      case "End":
        next = options.length - 1;
        break;
      default:
        return;
    }
    event.preventDefault();
    selectAt(next);
  }

  function handleChange(_event: MouseEvent<HTMLElement>, next: V | null) {
    if (next === null && !allowDeselect) return;
    onChange(next ?? undefined);
  }

  return (
    <Stack ref={groupRef} onKeyDown={onKeyDown}>
      <ToggleButtonGroup
        exclusive
        value={value ?? null}
        onChange={handleChange}
        aria-label={ariaLabel}
        role="radiogroup"
        size="small"
        sx={{ alignSelf: "flex-start", flexWrap: "wrap" }}
      >
        {options.map((option, index) => {
          const checked = value === option.value;
          return (
            <ToggleButton
              key={option.value}
              value={option.value}
              role="radio"
              aria-checked={checked}
              tabIndex={index === tabbableIndex ? 0 : -1}
            >
              {option.label}
            </ToggleButton>
          );
        })}
      </ToggleButtonGroup>
    </Stack>
  );
}
